import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';

// Animations
const floatWithRotate = keyframes`
  0% { transform: translate(0, 0) rotate(0deg); }
  25% { transform: translate(5px, -10px) rotate(2deg); }
  50% { transform: translate(0, -15px) rotate(0deg); }
  75% { transform: translate(-5px, -10px) rotate(-2deg); }
  100% { transform: translate(0, 0) rotate(0deg); }
`;

const pulseGlow = keyframes`
  0% { box-shadow: 0 0 5px 0 rgba(var(--primary-rgb), 0.3); }
  50% { box-shadow: 0 0 12px 3px rgba(var(--primary-rgb), 0.6); }
  100% { box-shadow: 0 0 5px 0 rgba(var(--primary-rgb), 0.3); }
`;

// Container for the floating DNA markers
const MarkersContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
`;

// DNA Marker representing founder type
const DnaMarker = styled(motion.div)`
  position: absolute;
  width: ${props => props.size || '30px'};
  height: ${props => props.size || '30px'};
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(${props => props.primaryColor || '45, 212, 191'}, 0.7) 0%,
    rgba(${props => props.secondaryColor || '37, 65, 178'}, 0.4) 100%
  );
  left: ${props => props.left || '10%'};
  top: ${props => props.top || '10%'};
  filter: blur(1px);
  animation: 
    ${floatWithRotate} ${props => props.floatDuration || '8s'} infinite ease-in-out,
    ${pulseGlow} ${props => props.pulseDuration || '4s'} infinite ease-in-out;
  animation-delay: ${props => props.delay || '0s'};
  opacity: ${props => props.opacity || 0.7};
  box-shadow: 0 0 8px 2px rgba(var(--primary-rgb), 0.3);
  z-index: ${props => props.zIndex || 1};
  
  &::before, &::after {
    content: '';
    position: absolute;
    width: ${props => props.connectionSize || '150%'};
    height: 1px;
    background: ${props => `linear-gradient(90deg, 
      rgba(${props.primaryColor || '45, 212, 191'}, 0.8), 
      rgba(${props.secondaryColor || '37, 65, 178'}, 0))`};
    top: 50%;
    left: ${props => props.left < 50 ? '100%' : 'auto'};
    right: ${props => props.left >= 50 ? '100%' : 'auto'};
    transform: ${props => props.left < 50 ? 'rotate(15deg)' : 'rotate(-15deg)'};
    transform-origin: ${props => props.left < 50 ? 'left' : 'right'} center;
  }
`;

// Connection Line between markers
const ConnectionLine = styled(motion.div)`
  position: absolute;
  width: ${props => props.width || '100px'};
  height: 1px;
  background: linear-gradient(90deg, 
    rgba(${props => props.startColor || '45, 212, 191'}, 0.6), 
    rgba(${props => props.endColor || '37, 65, 178'}, 0.6));
  transform-origin: center;
  transform: ${props => `rotate(${props.angle || '0deg'})`};
  left: ${props => props.left || '10%'};
  top: ${props => props.top || '10%'};
  box-shadow: 0 0 5px rgba(${props => props.shadowColor || '45, 212, 191'}, 0.4);
  opacity: ${props => props.opacity || 0.5};
`;

const FloatingDnaMarkers = ({ profilePercentages }) => {
  // Helper to calculate position based on percentage
  const calculatePosition = (percentage, index, total) => {
    // Creates a circular pattern
    const angle = (index / total) * 360;
    const radius = 250 + (percentage / 100) * 100; // Radius based on percentage
    
    return {
      left: `calc(50% + ${Math.cos(angle * Math.PI / 180) * radius}px)`,
      top: `calc(50% + ${Math.sin(angle * Math.PI / 180) * radius}px)`,
      size: `${20 + (percentage / 100) * 20}px` // Size based on percentage
    };
  };
  
  // Convert profile percentages to array for mapping
  const profileEntries = Object.entries(profilePercentages || {})
    .sort((a, b) => b[1] - a[1]); // Sort by percentage descending
  
  // Colors for different founder types
  const typeColors = {
    VISIONARY: '45, 212, 191', // Primary teal
    STRATEGIST: '37, 65, 178', // Secondary blue
    EXECUTOR: '213, 51, 105', // Pink
    INNOVATOR: '255, 153, 0', // Orange
    CONNECTOR: '76, 217, 100', // Green
    ANALYST: '147, 51, 234' // Purple
  };
  
  return (
    <MarkersContainer>
      {/* DNA Markers for each founder type */}
      {profileEntries.map(([type, percentage], index) => {
        const { left, top, size } = calculatePosition(
          percentage, 
          index, 
          profileEntries.length
        );
        
        return (
          <DnaMarker
            key={type}
            primaryColor={typeColors[type] || '45, 212, 191'}
            secondaryColor={typeColors[profileEntries[0][0]] || '37, 65, 178'}
            left={left}
            top={top}
            size={size}
            opacity={0.6 + (percentage / 100) * 0.4}
            floatDuration={`${8 + index}s`}
            pulseDuration={`${4 + index}s`}
            delay={`${index * 0.5}s`}
            zIndex={percentage > 50 ? 2 : 1}
          />
        );
      })}
      
      {/* Connection lines between primary and other markers */}
      {profileEntries.length > 1 && profileEntries.slice(1).map(([type, percentage], index) => {
        const primaryPos = calculatePosition(
          profileEntries[0][1],
          0, 
          profileEntries.length
        );
        const currentPos = calculatePosition(
          percentage,
          index + 1, 
          profileEntries.length
        );
        
        // Calculate angle between points
        const dx = parseFloat(currentPos.left) - parseFloat(primaryPos.left);
        const dy = parseFloat(currentPos.top) - parseFloat(primaryPos.top);
        const angle = Math.atan2(dy, dx) * (180 / Math.PI);
        
        // Calculate distance between points
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        return (
          <ConnectionLine
            key={`connection-${type}`}
            startColor={typeColors[profileEntries[0][0]]}
            endColor={typeColors[type]}
            shadowColor={typeColors[type]}
            left={`calc(${primaryPos.left} + ${parseFloat(primaryPos.size) / 2}px)`}
            top={`calc(${primaryPos.top} + ${parseFloat(primaryPos.size) / 2}px)`}
            width={`${distance}px`}
            angle={`${angle}deg`}
            opacity={0.3 + (percentage / 100) * 0.3}
          />
        );
      })}
    </MarkersContainer>
  );
};

export default FloatingDnaMarkers; 